import { Injectable } from '@angular/core';

import { ActivatedRouteSnapshot, Router } from '@angular/router';

import { catchError, concatMap, map } from 'rxjs/operators';
import { from, Observable, of } from 'rxjs';

import { Encryption } from '../encryption/encryption.types';
import { EncryptionService } from '../encryption/encryption.service';
import { License } from '../license/license.types';
import { SessionService } from '@app/services/session.service';
import { SettingsService } from '@app/services/settings.service';
import { SpeakseeOrderService } from '../license/license.service';

/**
 * Resolver for the session route.
 * This resolver is responsible for resolving the session data before
 * navigating to the session route.
 */
@Injectable({
  providedIn: 'root',
})
export class SessionResolver  {
  constructor(
    private router: Router,
    private orderService: SpeakseeOrderService,
    private sessionService: SessionService,
    private settingsService: SettingsService,
    private encryptionService: EncryptionService
  ) {}

  /**
   * Resolves the session data.
   *
   * @param route - The activated route snapshot.
   * @returns An observable of the resolved license.
   */
  resolve(route: ActivatedRouteSnapshot): Observable<License> {
    const id = route.params.id;

    if (localStorage.getItem('auth_mode') === 'shared-session') {
      const license: License = {
        description: 'Shared session guest',
        firestoredb: '',
        id: 0,
        language: '',
        uuid: id,
        type: 'soundshape',
      };
      return of(license);
    }

    // Use the same lookup path for both license IDs and device UUIDs
    // This ensures we get the correct license type for Soundshape devices
    return this.orderService.getLicenseByIdOrSerial(id).pipe(
      concatMap((license: License) =>
        from(this.encryptionService.getOrCreateKeys(license.serial ?? license?.serials[0])).pipe(
          map((encryption: Encryption) => {
            license.encryption = encryption;
            return license;
          })
        )
      ),
      concatMap((license: License) => {
        if (license.type === 'microphonekit') {
          return this.settingsService
            .getRemoteSetSettings(license.id, true)
            .pipe(map(() => license));
        }
        return of(license);
      }),
      concatMap((license: License) =>
        this.sessionService.startSession(license).pipe(map(() => license))
      ),
      catchError((error: any) => {
        console.error(error);
        this.router.navigate(['/microphone-kits']);
        throw error;
      })
    );
  }
}
